/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Native-app deep link helpers.
 *
 * Used by ResetPasswordPage to hand an `application` user back to the native app after they
 * finish resetting their password in the browser.
 *
 * Why an `intent://` URL rather than an Android App Link: App Links must be verified against a
 * real HTTPS domain serving /.well-known/assetlinks.json. The app frontend is currently deployed
 * on a bare IP over plain HTTP (see FRONTEND_RESET_PASSWORD_URL_APP), so verification is
 * impossible and a custom scheme is the only option. Revisit if this app ever gets a domain.
 */

import envConfig from "../config/envConfig";

/**
 * Chrome on Android is the only browser that understands `intent://`. On every other
 * browser/platform the URL is meaningless, so callers must gate on this before offering to
 * open the app.
 */
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

/**
 * Builds a Chrome intent URL that opens the native app, falling back to a normal web URL when
 * the app is not installed.
 *
 * Shape: intent://<hostPath>#Intent;scheme=<scheme>;package=<pkg>;S.browser_fallback_url=<url>;end
 *
 * `package` pins the intent to our app so no other installed app can claim the scheme. The
 * fallback is what makes this safe to show to someone who doesn't have the app: instead of a dead
 * link or a browser error, they land on a real page.
 *
 * @returns {string|null} the intent URL, or null if deep linking isn't configured
 */
export function buildAppLoginIntentUrl() {
  const { androidAppScheme, androidAppPackage, androidAppFallbackUrl } = envConfig;

  // Without a scheme and package there is no app to open — callers should fall back to the web.
  if (!androidAppScheme || !androidAppPackage) return null;

  const parts = [
    `scheme=${androidAppScheme}`,
    `package=${androidAppPackage}`,
  ];

  // Optional: without a fallback, Chrome shows its own error page when the app is missing.
  if (androidAppFallbackUrl) {
    parts.push(`S.browser_fallback_url=${encodeURIComponent(androidAppFallbackUrl)}`);
  }

  return `intent://auth/login#Intent;${parts.join(";")};end`;
}

/**
 * True when the reset was completed by a native-app user on a device that can actually open it.
 * Admin/portal users and desktop browsers keep the normal in-app web routing.
 */
export function canOpenAppLogin(userType) {
  return userType === "application" && isAndroid() && buildAppLoginIntentUrl() !== null;
}

/**
 * Navigates to the app. Must be called from a user gesture (a click) — browsers block or throttle
 * custom-scheme navigation that isn't tied to one, which is why the reset page offers a button
 * rather than auto-redirecting.
 */
export function openAppLogin() {
  const url = buildAppLoginIntentUrl();
  if (!url) return false;
  window.location.href = url;
  return true;
}
